import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { BarChart3, TrendingUp, AlertTriangle } from "lucide-react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Spinner } from "@/components/ui/spinner";
import { getCategories, getWords } from "@/hooks/useFirestore";
import { useAuth } from "@/contexts/AuthContext";
import { calculateStrength, cn } from "@/lib/utils";

export function StatsPage() {
  const { user } = useAuth();

  const { data: categories, isLoading: categoriesLoading } = useQuery({
    queryKey: ["categories", user?.uid],
    queryFn: () => getCategories(user!.uid),
    enabled: !!user,
  });

  const { data: words, isLoading: wordsLoading } = useQuery({
    queryKey: ["words", user?.uid],
    queryFn: () => getWords(user!.uid),
    enabled: !!user,
  });

  // Calculate stats from words
  const stats = useMemo(() => {
    if (!words) return { totalWords: 0, reviewedWords: 0, averageSuccessRate: 0 };

    let reviewedWords = 0;
    let totalKnown = 0;
    let totalReviews = 0;

    words.forEach((word) => {
      const reviewed = word.reviewStats.knownCount + word.reviewStats.unknownCount;
      if (reviewed > 0) {
        reviewedWords++;
        totalKnown += word.reviewStats.knownCount;
        totalReviews += reviewed;
      }
    });

    return {
      totalWords: words.length,
      reviewedWords,
      averageSuccessRate: totalReviews > 0 ? totalKnown / totalReviews : 0,
    };
  }, [words]);

  const isLoading = categoriesLoading || wordsLoading;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  // Calculate category stats
  const categoryStats = categories?.map((cat) => {
    const categoryWords = words?.filter((w) => w.categories.includes(cat.name)) || [];
    const reviewed = categoryWords.filter(
      (w) => w.reviewStats.knownCount + w.reviewStats.unknownCount > 0
    );
    const totalKnown = reviewed.reduce((sum, w) => sum + w.reviewStats.knownCount, 0);
    const totalReviews = reviewed.reduce(
      (sum, w) => sum + w.reviewStats.knownCount + w.reviewStats.unknownCount,
      0
    );
    const successRate = totalReviews > 0 ? totalKnown / totalReviews : 0;

    return {
      ...cat,
      successRate,
      reviewedCount: reviewed.length,
    };
  });

  // Find weakest categories (lowest success rate with at least some reviews)
  const weakestCategories = categoryStats
    ?.filter((c) => c.reviewedCount > 0)
    .sort((a, b) => a.successRate - b.successRate)
    .slice(0, 5);

  // Find words most needing review: due words first, then by most lapses
  const hardestWords = words
    ?.slice()
    .sort((a, b) => {
      const now = Date.now();
      const aDue = !a.reviewStats.nextReviewAt ? 0 :
        ((a.reviewStats.nextReviewAt as any).toMillis?.() ?? new Date(a.reviewStats.nextReviewAt).getTime());
      const bDue = !b.reviewStats.nextReviewAt ? 0 :
        ((b.reviewStats.nextReviewAt as any).toMillis?.() ?? new Date(b.reviewStats.nextReviewAt).getTime());
      const aIsDue = aDue <= now ? 1 : 0;
      const bIsDue = bDue <= now ? 1 : 0;
      // Due words first
      if (aIsDue !== bIsDue) return bIsDue - aIsDue;
      // Then by most lapses
      return (b.reviewStats.lapses ?? 0) - (a.reviewStats.lapses ?? 0);
    })
    .slice(0, 10);

  // Calculate strength distribution
  const strengthDistribution = {
    weak: 0,
    learning: 0,
    strong: 0,
  };

  words?.forEach((word) => {
    const strength = calculateStrength(
      word.reviewStats.knownCount,
      word.reviewStats.unknownCount
    );
    strengthDistribution[strength]++;
  });

  const total = words?.length || 1;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Statistics</h1>
        <p className="text-muted-foreground">
          Track your learning progress
        </p>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4 text-center">
            <div className="text-3xl font-bold">{stats?.totalWords || 0}</div>
            <div className="text-sm text-muted-foreground">Total Words</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 text-center">
            <div className="text-3xl font-bold">{stats?.reviewedWords || 0}</div>
            <div className="text-sm text-muted-foreground">Reviewed</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 text-center">
            <div className="text-3xl font-bold">
              {Math.round((stats?.averageSuccessRate || 0) * 100)}%
            </div>
            <div className="text-sm text-muted-foreground">Success Rate</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 text-center">
            <div className="text-3xl font-bold">{categories?.length || 0}</div>
            <div className="text-sm text-muted-foreground">Categories</div>
          </CardContent>
        </Card>
      </div>

      {/* Strength Distribution */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            Strength Distribution
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Bars */}
            <div className="space-y-3">
              <StrengthBar
                label="Strong"
                count={strengthDistribution.strong}
                total={total}
                color="bg-green-500"
              />
              <StrengthBar
                label="Learning"
                count={strengthDistribution.learning}
                total={total}
                color="bg-amber-500"
              />
              <StrengthBar
                label="Weak"
                count={strengthDistribution.weak}
                total={total}
                color="bg-red-500"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Weakest Categories */}
      {weakestCategories && weakestCategories.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Categories Needing Practice
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {weakestCategories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/categories/${cat.id}`}
                  className="flex items-center justify-between p-2 rounded-md hover:bg-muted/50"
                >
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{cat.name}</span>
                    <Badge variant="secondary">{cat.wordCount} words</Badge>
                  </div>
                  <div className="text-sm">
                    <span
                      className={cn(
                        cat.successRate < 0.5
                          ? "text-red-600"
                          : cat.successRate < 0.7
                          ? "text-amber-600"
                          : "text-green-600"
                      )}
                    >
                      {Math.round(cat.successRate * 100)}% success
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Words Needing Review */}
      {hardestWords && hardestWords.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Words Needing Review
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {hardestWords.map((word) => {
                const strength = calculateStrength(
                  word.reviewStats.knownCount,
                  word.reviewStats.unknownCount
                );
                return (
                  <Link
                    key={word.id}
                    to={`/words/${word.id}`}
                    className="flex items-center justify-between p-2 rounded-md hover:bg-muted/50"
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={cn(
                          "w-2 h-2 rounded-full",
                          strength === "weak" && "bg-red-500",
                          strength === "learning" && "bg-amber-500",
                          strength === "strong" && "bg-green-500"
                        )}
                      />
                      <span className="font-medium hebrew-text" dir="rtl">
                        {word.hebrew.text}
                      </span>
                      <span className="text-muted-foreground">
                        {word.translations.en?.text || word.translations.ru?.text}
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {word.reviewStats.knownCount}/{word.reviewStats.knownCount + word.reviewStats.unknownCount}
                    </div>
                  </Link>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function StrengthBar({
  label,
  count,
  total,
  color,
}: {
  label: string;
  count: number;
  total: number;
  color: string;
}) {
  const percentage = total > 0 ? (count / total) * 100 : 0;

  return (
    <div className="space-y-1">
      <div className="flex justify-between text-sm">
        <span>{label}</span>
        <span className="text-muted-foreground">
          {count} ({Math.round(percentage)}%)
        </span>
      </div>
      <div className="h-3 bg-muted rounded-full overflow-hidden">
        <div
          className={cn("h-full transition-all duration-500", color)}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
}
